import React, { useCallback, useContext, useEffect, useMemo } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles, IGetRestaurantsShort, IGetRestaurantsShortArgs } from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useFetchData } from 'hooks/useFetchData';
import { useRoleAccess } from 'hooks/useRoleAccess';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantsMultipleSelect.module.scss';

interface IRestaurantsMultipleSelect {
	setRestaurants: (value: string[]) => void;
	setFetchedRestaurants: (value: IItemForGetShortInfo[]) => void;
	selectedCustomer: Nullable<string>;
	selectedRestaurants: string[];
}

const RestaurantsMultipleSelect: React.FC<IRestaurantsMultipleSelect> = ({
	selectedRestaurants,
	selectedCustomer,
	setRestaurants,
	setFetchedRestaurants,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		selectedCustomer && hasAccess ? getRestaurantsShortInfo : null,
		{
			customerId: selectedCustomer || '',
			menuApiVersion: 'v1',
		},
	);

	const restaurants = useMemo(() => {
		return hasAccess ? response?.data.restaurants : loggedUser.restaurants;
	}, [hasAccess, response, loggedUser]);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = selectedRestaurants.filter((el) => el !== id);
			setRestaurants(updatedSelectedItems);
		},
		[selectedRestaurants, setRestaurants],
	);

	const restaurantsList = useMemo(() => {
		return selectedRestaurants.map((el) => {
			const label = restaurants?.find((rest) => rest.id === el)?.name || '';
			return <SelectedItem key={el} label={label} onDelete={() => handleRemove(el)} />;
		});
	}, [selectedRestaurants, restaurants]);

	useEffect(() => {
		refetch();
	}, [selectedCustomer]);

	useEffect(() => {
		if (response) {
			setFetchedRestaurants(response.data.restaurants);
		} else if (!hasAccess) {
			const accessibleRestaurants = loggedUser.restaurants ? loggedUser.restaurants : [];
			setFetchedRestaurants(accessibleRestaurants);
		}
	}, [response, loggedUser, hasAccess, selectedCustomer]);

	return (
		<div className={style.container} data-test="RestaurantsMultipleSelect">
			<div className={style.field}>
				<MultipleSelect
					data={restaurants || []}
					isLoading={loading}
					refetch={refetch}
					responseError={responseError}
					selectedCustomer={selectedCustomer}
					selectedRestaurants={selectedRestaurants}
					setRestaurants={setRestaurants}
				/>
			</div>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantsMultipleSelect);
